import { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MasterAdminAuthProvider, useMasterAdmin } from "@/context/MasterAdminAuthContext";
import { autoPrintService } from "@/lib/autoPrintService";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppSplash from "@/components/AppSplash";
import LicenseGate from "@/components/LicenseGate";

import ScanStudio from "@/pages/ScanStudio";
import PrintJobsQueue from "@/pages/PrintJobsQueue";
import PrinterManagement from "@/pages/PrinterManagement";
import ShopQrSetup from "@/pages/ShopQrSetup";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import ScanToPrintCustomer from "@/pages/ScanToPrintCustomer";

// Master Admin Sub-pages
import MasterAdminLogin from "@/pages/master-admin/MasterAdminLogin";
import MasterAdminSetup from "@/pages/master-admin/MasterAdminSetup";
import MasterAdminLayout from "@/pages/master-admin/MasterAdminLayout";
import MasterAdminDashboard from "@/pages/master-admin/MasterAdminDashboard";
import MasterAdminUsers from "@/pages/master-admin/MasterAdminUsers";
import MasterAdminMemberships from "@/pages/master-admin/MasterAdminMemberships";
import MasterAdminLicenses from "@/pages/master-admin/MasterAdminLicenses";
import MasterAdminDevices from "@/pages/master-admin/MasterAdminDevices";
import MasterAdminPayments from "@/pages/master-admin/MasterAdminPayments";
import MasterAdminAuditLogs from "@/pages/master-admin/MasterAdminAuditLogs";
import MasterAdminSettings from "@/pages/master-admin/MasterAdminSettings";

// ======================================================
// HOME ROUTE - Directly launches ScanStudio
// ======================================================

function HomeRoute() {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <ScanStudio />;
}

function MasterAdminProtectedRoute({ children }) {
  const { masterAdmin, loading } = useMasterAdmin();
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400">
        <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!masterAdmin) {
    return <Navigate to="/master-admin/login" replace />;
  }
  return children;
}

// ======================================================
// MAIN APP
// ======================================================

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    return sessionStorage.getItem("printq_splash_seen") !== "1";
  });

  useEffect(() => {
    autoPrintService.start();
    return () => autoPrintService.stop();
  }, []);

  return (
    <div className="App">
      {/* ==================================================
          PRINTQ SPLASH SCREEN
         ================================================== */}
      {showSplash && (
        <AppSplash
          onComplete={() => {
            sessionStorage.setItem("printq_splash_seen", "1");
            setShowSplash(false);
          }}
        />
      )}

      {/* ==================================================
          AUTH PROVIDERS (NORMAL + MASTER ADMIN)
         ================================================== */}
      <AuthProvider>
        <MasterAdminAuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: "#18181b",
                  border: "1px solid #27272a",
                  color: "#fff",
                },
              }}
            />

            {/* ==================================================
                APPLICATION ROUTES
               ================================================== */}
            <Routes>
              {/* PUBLIC LOGIN */}
              <Route path="/login" element={<Login />} />

              {/* MASTER ADMIN AUTH ROUTES */}
              <Route path="/master-admin/login" element={<MasterAdminLogin />} />
              <Route path="/master-admin/setup" element={<MasterAdminSetup />} />

              {/* MASTER ADMIN WORKSPACE (DEDICATED LAYOUT & PROTECTION) */}
              <Route
                path="/master-admin"
                element={
                  <MasterAdminProtectedRoute>
                    <MasterAdminLayout />
                  </MasterAdminProtectedRoute>
                }
              >
                <Route index element={<MasterAdminDashboard />} />
                <Route path="users" element={<MasterAdminUsers />} />
                <Route path="memberships" element={<MasterAdminMemberships />} />
                <Route path="licenses" element={<MasterAdminLicenses />} />
                <Route path="devices" element={<MasterAdminDevices />} />
                <Route path="payments" element={<MasterAdminPayments />} />
                <Route path="audit-logs" element={<MasterAdminAuditLogs />} />
                <Route path="settings" element={<MasterAdminSettings />} />
              </Route>

              {/* PUBLIC CUSTOMER SCAN TO PRINT (QR Mobile) */}
              <Route
                path="/scan-to-print/:shopId"
                element={<ScanToPrintCustomer />}
              />

              {/* PROTECTED DESKTOP STUDIO WORKSPACE (WITH LICENSE GATE) */}
              <Route
                element={
                  <ProtectedRoute allowedRoles={["admin", "user"]}>
                    <LicenseGate>
                      <Layout />
                    </LicenseGate>
                  </ProtectedRoute>
                }
              >
                {/* PRIMARY SCAN TO PRINT STUDIO */}
                <Route index element={<HomeRoute />} />
                <Route path="/scan-to-print" element={<ScanStudio />} />

                {/* PRINT JOBS QUEUE */}
                <Route path="/jobs" element={<PrintJobsQueue />} />

                {/* PRINTER SETUP & MANAGEMENT */}
                <Route path="/printers" element={<PrinterManagement />} />

                {/* CUSTOMER QR & RATES */}
                <Route path="/qr-setup" element={<ShopQrSetup />} />

                {/* SHOP SETTINGS */}
                <Route path="/settings" element={<Settings />} />

                {/* SAFE CATCH-ALL REDIRECT */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </MasterAdminAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;